import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, downloadPdf, formatMoney, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EstadoBadge from "@/components/EstadoBadge";
import EstadoPagoBadge from "@/components/EstadoPagoBadge";
import PagosPanel from "@/components/PagosPanel";
import ProductoCombobox from "@/components/ProductoCombobox";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Trash2, Plus, CheckCircle2, FileDown, Ban, Save, AlertTriangle, Copy, PackageCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function PedidoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [remito, setRemito] = useState(null);

  const load = async () => {
    const [p, ps] = await Promise.all([
      api.get(`/pedidos/${id}`),
      api.get("/productos"),
    ]);
    setPedido(p.data);
    setItems(p.data.items.map((it, idx) => ({ ...it, _key: it.id || `it-${idx}` })));
    setProductos(ps.data);
    try {
      const remitos = await api.get("/remitos");
      const r = remitos.data.find((x) => x.pedido_id === p.data.id);
      if (r) setRemito(r);
    } catch (err) {
      console.warn("No se pudo cargar el remito asociado:", err);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!pedido) {
    return <div className="text-[#8C8C8C]">Cargando pedido...</div>;
  }

  const updateItem = (key, patch) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it._key !== key) return it;
        const next = { ...it, ...patch };
        next.subtotal = Number(next.cantidad || 0) * Number(next.precio_unitario || 0);
        return next;
      })
    );
  };

  const setProducto = (key, p) => {
    if (!p) return;
    updateItem(key, {
      producto_id: p.id,
      producto_nombre: p.nombre,
      precio_unitario: Number(p.precio),
      needs_review: false,
      reason: null,
      candidates: [],
    });
  };
  const setProductoById = (key, productoId) => {
    const p = productos.find((x) => x.id === productoId);
    setProducto(key, p);
  };

  const removeItem = (key) =>
    setItems((prev) => prev.filter((it) => it._key !== key));

  const addItem = () =>
    setItems((prev) => [
      ...prev,
      {
        _key: `new-${Date.now()}`,
        producto_id: null,
        producto_nombre: "",
        cantidad: 1,
        precio_unitario: 0,
        subtotal: 0,
        needs_review: true,
        raw_text: "",
        reason: "Sin producto seleccionado",
        candidates: [],
      },
    ]);

  const total = items.reduce((s, it) => s + Number(it.subtotal || 0), 0);
  const totalUnidades = items.reduce((s, it) => s + Number(it.cantidad || 0), 0);
  const hasReview = items.some((it) => it.needs_review || !it.producto_id);
  const isLocked = pedido.estado === "ENTREGADO" || pedido.estado === "CANCELADO";
  const isConfirmed = pedido.estado === "CONFIRMADO";
  const isPending = pedido.estado === "PENDIENTE";

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map(({ _key, candidates, ...rest }) => ({
          ...rest,
          cantidad: Number(rest.cantidad),
          precio_unitario: Number(rest.precio_unitario),
          subtotal: Number(rest.subtotal),
        })),
      };
      const res = await api.put(`/pedidos/${id}`, payload);
      setPedido(res.data);
      toast.success("Pedido actualizado.");
    } catch {
      toast.error("Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const confirmar = async () => {
    if (hasReview) {
      toast.error("Corregí los items en revisión antes de confirmar.");
      return;
    }
    try {
      await guardar();
      const res = await api.post(`/pedidos/${id}/confirmar`);
      setRemito(res.data.remito);
      setPedido((p) => ({ ...p, estado: "CONFIRMADO" }));
      toast.success("Pedido confirmado. Remito generado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al confirmar.");
    }
  };

  const cancelar = async () => {
    try {
      await api.put(`/pedidos/${id}`, { estado: "CANCELADO" });
      setPedido((p) => ({ ...p, estado: "CANCELADO" }));
      toast.success("Pedido cancelado.");
    } catch {
      toast.error("Error al cancelar.");
    }
  };

  const eliminar = async () => {
    try {
      await api.delete(`/pedidos/${id}`);
      toast.success("Pedido eliminado.");
      navigate("/admin/pedidos");
    } catch {
      toast.error("Error al eliminar.");
    }
  };

  const marcarEntregado = async () => {
    try {
      await api.post(`/pedidos/${id}/estado`, null, { params: { estado: "ENTREGADO" } });
      setPedido((p) => ({ ...p, estado: "ENTREGADO" }));
      toast.success("Pedido marcado como entregado.");
    } catch {
      toast.error("Error al cambiar estado.");
    }
  };

  const duplicar = async () => {
    try {
      const res = await api.post(`/pedidos/${id}/duplicar`);
      toast.success(`Pedido duplicado: ${res.data.pedido_id}`);
      navigate(`/admin/pedidos/${res.data.id}`);
    } catch {
      toast.error("Error al duplicar.");
    }
  };

  const descargarRemito = async () => {
    if (!remito) return;
    try {
      await downloadPdf(`/remitos/${remito.id}/pdf`, `remito_${remito.remito_id}.pdf`);
    } catch {
      toast.error("Error al descargar PDF.");
    }
  };

  return (
    <div className="pb-28 lg:pb-0">
      <Link to="/admin/pedidos" className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#C85A32] mb-4" data-testid="pd-back">
        <ArrowLeft className="h-4 w-4" /> Volver a pedidos
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-2 flex items-center gap-2 flex-wrap">
            Pedido <span className="font-mono">{pedido.pedido_id}</span>
            <EstadoBadge estado={pedido.estado} />
            <EstadoPagoBadge estado={pedido.estado_pago} />
          </div>
          <h1 className="font-display font-bold text-4xl text-[#1A1A1A]">{pedido.cliente}</h1>
          <p className="text-[#595959] text-sm mt-1">
            Fecha: <span className="font-semibold">{formatDate(pedido.fecha)}</span>
            {pedido.telefono && <> · Tel: <span className="font-mono">{pedido.telefono}</span></>}
          </p>
        </div>
        <div className="hidden lg:flex flex-wrap gap-2">
          {remito && (
            <Button
              variant="outline"
              className="border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2"
              onClick={descargarRemito}
              data-testid="pd-descargar-remito"
            >
              <FileDown className="h-4 w-4" /> Descargar PDF
            </Button>
          )}
          <Button
            variant="outline"
            className="border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2"
            onClick={duplicar}
            data-testid="pd-duplicar"
          >
            <Copy className="h-4 w-4" /> Duplicar
          </Button>
          {!isLocked && (
            <>
              {isPending && (
                <Button
                  variant="outline"
                  className="border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2"
                  onClick={guardar}
                  disabled={saving}
                  data-testid="pd-guardar"
                >
                  <Save className="h-4 w-4" /> Guardar
                </Button>
              )}
              {isPending && (
                <Button
                  className="bg-[#6B705C] hover:bg-[#545848] text-white gap-2"
                  onClick={confirmar}
                  disabled={hasReview}
                  data-testid="pd-confirmar"
                >
                  <CheckCircle2 className="h-4 w-4" /> Confirmar
                </Button>
              )}
              {isConfirmed && (
                <Button
                  className="bg-[#3F6B3C] hover:bg-[#2F5A2D] text-white gap-2"
                  onClick={marcarEntregado}
                  data-testid="pd-entregar"
                >
                  <PackageCheck className="h-4 w-4" /> Marcar entregado
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="text-[#8E2E2E] hover:bg-[#FBEBEB] gap-2" data-testid="pd-cancelar">
                    <Ban className="h-4 w-4" /> Cancelar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancelar pedido</AlertDialogTitle>
                    <AlertDialogDescription>
                      ¿Marcar este pedido como CANCELADO? Esta acción no genera remito.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Volver</AlertDialogCancel>
                    <AlertDialogAction onClick={cancelar} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Cancelar pedido</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-[#595959] hover:text-[#8E2E2E] hover:bg-[#FBEBEB] gap-2" data-testid="pd-eliminar">
                <Trash2 className="h-4 w-4" /> Eliminar
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar pedido</AlertDialogTitle>
                <AlertDialogDescription>
                  Esto elimina el pedido y su remito (si tuviera). No se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Volver</AlertDialogCancel>
                <AlertDialogAction onClick={eliminar} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-[#E5E0D8] rounded-2xl bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl text-[#1A1A1A]">Items</h2>
            {hasReview && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full bg-[#FDF3E1] text-[#A6751F] border border-[#F0DEB2]">
                <AlertTriangle className="h-3.5 w-3.5" /> Revisar
              </span>
            )}
          </div>
          <div className="space-y-3">
            {items.map((it, idx) => (
              <ItemRow
                key={it._key}
                item={it}
                index={idx}
                productos={productos}
                locked={isLocked}
                onPickCandidate={(p) => setProducto(it._key, p)}
                onChangeProducto={(pid) => setProductoById(it._key, pid)}
                onChangeQty={(q) => updateItem(it._key, { cantidad: Math.max(1, Number(q || 1)) })}
                onChangePrecio={(p) => updateItem(it._key, { precio_unitario: Math.max(0, Number(p || 0)) })}
                onRemove={() => removeItem(it._key)}
              />
            ))}
            {!isLocked && (
              <button
                type="button"
                onClick={addItem}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-[#E5E0D8] text-sm text-[#595959] hover:bg-[#F9F6F0] transition-colors"
                data-testid="pd-add-item"
              >
                <Plus className="h-4 w-4" /> Agregar item
              </button>
            )}
          </div>
        </Card>

        <Card className="p-6 border-[#E5E0D8] rounded-2xl bg-white h-fit">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-2">Total · {totalUnidades} u</div>
          <div className="font-display font-bold text-4xl text-[#C85A32] mb-6" data-testid="pd-total">
            {formatMoney(total)}
          </div>

          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-2">Texto original</div>
          <div className="font-mono text-xs bg-[#F9F6F0] border border-[#E5E0D8] rounded-lg p-3 whitespace-pre-wrap text-[#1A1A1A]" data-testid="pd-texto-original">
            {pedido.texto_original || <span className="text-[#8C8C8C] italic">(sin texto)</span>}
          </div>

          {remito && (
            <div className="mt-5 pt-5 border-t border-[#E5E0D8]">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-1">Remito</div>
              <div className="font-mono font-semibold text-[#1A1A1A]">{remito.remito_id}</div>
            </div>
          )}
        </Card>
      </div>

      {/* Pagos panel (only after pedido has a total + items) */}
      {!isPending || pedido.total > 0 ? (
        <div className="mt-6">
          <PagosPanel pedido={pedido} onChange={(updated) => setPedido(updated)} />
        </div>
      ) : null}

      {/* Mobile sticky bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-[#E5E0D8] px-4 py-3 flex items-center justify-between gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">{totalUnidades} u · Total</div>
          <div className="font-display font-bold text-xl text-[#C85A32] leading-tight">{formatMoney(total)}</div>
        </div>
        <div className="flex gap-1.5">
          {remito && (
            <Button variant="outline" size="sm" className="border-[#E5E0D8] h-10 px-3" onClick={descargarRemito} data-testid="pd-pdf-mobile">
              <FileDown className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" size="sm" className="border-[#E5E0D8] h-10 px-3" onClick={duplicar} data-testid="pd-dup-mobile">
            <Copy className="h-4 w-4" />
          </Button>
          {isPending && (
            <>
              <Button variant="outline" size="sm" className="border-[#E5E0D8] h-10" onClick={guardar} disabled={saving} data-testid="pd-guardar-mobile">
                Guardar
              </Button>
              <Button size="sm" className="bg-[#6B705C] hover:bg-[#545848] text-white h-10" onClick={confirmar} disabled={hasReview} data-testid="pd-confirmar-mobile">
                Confirmar
              </Button>
            </>
          )}
          {isConfirmed && (
            <Button size="sm" className="bg-[#3F6B3C] hover:bg-[#2F5A2D] text-white h-10" onClick={marcarEntregado} data-testid="pd-entregar-mobile">
              Entregado
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemRow({ item, index, productos, locked, onPickCandidate, onChangeProducto, onChangeQty, onChangePrecio, onRemove }) {
  const review = item.needs_review || !item.producto_id;
  const cands = item.candidates || [];
  return (
    <div
      className={`fade-row rounded-xl border p-3 sm:p-4 ${
        review ? "border-[#F0DEB2] bg-[#FDF8EE]" : "border-[#E5E0D8] bg-white"
      }`}
      style={{ animationDelay: `${index * 25}ms` }}
      data-testid={`pd-item-${index}`}
    >
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3 sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Cant.</Label>
          <Input
            type="number"
            min={1}
            value={item.cantidad}
            disabled={locked}
            onChange={(e) => onChangeQty(e.target.value)}
            className="h-10 text-center font-semibold"
            data-testid={`pd-item-${index}-cantidad`}
          />
        </div>
        <div className="col-span-9 sm:col-span-6">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Producto</Label>
          {locked ? (
            <div className="h-10 flex items-center px-3 border border-[#E5E0D8] rounded-md bg-[#F9F6F0] text-sm">{item.producto_nombre}</div>
          ) : (
            <ProductoCombobox
              value={item.producto_id || null}
              productos={productos}
              onChange={(p) => onChangeProducto(p.id)}
              review={review}
              currentName={item.producto_nombre}
              testid={`pd-item-${index}-producto`}
            />
          )}
        </div>
        <div className="col-span-7 sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Precio</Label>
          <Input
            type="number"
            value={item.precio_unitario}
            disabled={locked}
            onChange={(e) => onChangePrecio(e.target.value)}
            className="h-10 font-mono"
            data-testid={`pd-item-${index}-precio`}
          />
        </div>
        <div className="col-span-3 sm:col-span-1 text-right">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Subtotal</Label>
          <div className="h-10 flex items-center justify-end font-mono font-semibold text-[#1A1A1A]">
            {formatMoney(item.subtotal)}
          </div>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-end">
          {!locked && (
            <button
              type="button"
              onClick={onRemove}
              className="h-10 w-10 rounded-md hover:bg-[#FBEBEB] text-[#8E2E2E] flex items-center justify-center transition-colors"
              aria-label="Quitar item"
              data-testid={`pd-item-${index}-remove`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {review && (
        <div className="mt-2.5">
          <div className="flex items-center gap-2 text-[12px] text-[#A6751F]">
            <AlertTriangle className="h-3.5 w-3.5" />
            {item.reason || "Necesita revisión"}
            {item.raw_text && (
              <span className="font-mono text-[#8C8C8C]">— texto: "{item.raw_text}"</span>
            )}
          </div>
          {cands.length > 0 && !locked && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`pd-item-${index}-candidates`}>
              <span className="text-[10px] uppercase tracking-wider text-[#8C8C8C] self-center mr-1">¿Cuál?</span>
              {cands.map((c) => (
                <button
                  key={c.producto_id}
                  type="button"
                  onClick={() =>
                    onPickCandidate(
                      productos.find((p) => p.id === c.producto_id) || {
                        id: c.producto_id,
                        nombre: c.producto_nombre,
                        precio: c.precio,
                      }
                    )
                  }
                  className="px-2.5 py-1 rounded-full bg-white border border-[#E5E0D8] text-[12px] text-[#1A1A1A] hover:bg-[#F4EAE6] hover:border-[#C85A32] transition-colors"
                  data-testid={`pd-item-${index}-cand-${c.producto_id}`}
                >
                  <span className="font-medium">{c.producto_nombre}</span>{" "}
                  <span className="text-[#8C8C8C] font-mono text-[11px]">{formatMoney(c.precio)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
